import { CHAT_FUNCTIONS } from '../../../config/chatFunctionNames';

/**
 * Detect a proactive organization-event message.
 *
 * Three signals are checked so the card works in every case:
 *   - Live (just-arrived): `function_name` set
 *   - History reload:      `intent` preserved, `function_name` may be null
 *   - SMS-mirror / raw:    `raw_output.type` always present
 *
 * Distinct from `organization_events` which carries an event LIST. Proactive
 * messages always carry a single `raw_output.event_card`.
 */
export function isOrganizationEventProactiveMessage(message) {
  if (!message) return false;
  const target = CHAT_FUNCTIONS.ORGANIZATION_EVENT_PROACTIVE;
  return (
    message.function_name === target ||
    message.intent === target ||
    message.raw_output?.type === target
  );
}

/**
 * Extract the proactive event card into a normalized object (camelCase keys).
 * Returns null when `raw_output.event_card` is missing — that case is plain
 * status text (e.g. "Event got cancelled") and renders as a normal message.
 *
 * @returns {{
 *   title: string,
 *   dateLine: string,
 *   timeLine: string,
 *   locationName: string | null,
 *   where: string | null,
 *   link: string | null,
 *   description: string,
 *   imageUrl: string | null,
 *   officerndEventId: string | null,
 * } | null}
 */
export function getProactiveEventData(message) {
  if (!isOrganizationEventProactiveMessage(message)) return null;

  const card = message?.raw_output?.event_card;
  if (!card || typeof card !== 'object') return null;

  return {
    title: card.title || '',
    dateLine: card.date_line || '',
    timeLine: card.time_line || '',
    locationName: card.location_name ?? null,
    where: card.where ?? null,
    link: card.link ?? null,
    description: card.description || '',
    imageUrl: card.image_url ?? null,
    officerndEventId: card.officernd_event_id ?? null,
  };
}

/**
 * Header text the typewriter should show above the card.
 * Returns null when the message has no event_card payload (status text case).
 */
export function getProactiveEventHeader(message) {
  if (!getProactiveEventData(message)) return null;
  return message?.raw_output?.header ?? null;
}
